import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const TermeniConditii = () => {
  return (
    <div className="min-h-screen bg-[#1a1a1a] text-amber-100">
      <SEOHead
        title="Termeni și Condiții — Casa Cartarum"
        description="Termenii și condițiile de utilizare a site-ului Casa Cartarum — galerie interactivă de hărți istorice ale României."
        canonical="https://www.casacartarum.ro/termeni-conditii"
      />
      {/* Header with Back Button */}
      <div className="bg-gradient-to-b from-[#2a2520] to-[#1a1a1a] py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <Link 
            to="/#contact" 
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors mb-6"
            data-testid="back-to-contact"
          >
            <ArrowLeft size={20} />
            <span>Înapoi la Contact</span>
          </Link>
          
          <h1 className="text-4xl md:text-5xl font-bold text-amber-100 mb-4">
            Termeni și Condiții
          </h1>
          <p className="text-amber-200/70 text-lg">
            Casa Cartarum - Asociația Culturală Dobrogenius
          </p>
          <p className="text-amber-200/50 text-sm mt-2">
            Ultima actualizare: 15 februarie 2026
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-invert prose-amber max-w-none">
          
          {/* Section 1: Informații Generale */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              1. INFORMAȚII GENERALE
            </h2>
            
            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">1.1 Despre Noi</h3>
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Casa Cartarum este o platformă digitală culturală deținută și operată de <strong>Asociația Culturală Dobrogenius</strong>, cu sediul în România.
            </p>
            
            <div className="bg-amber-900/20 p-6 rounded-lg border border-amber-800/30 mb-6">
              <h4 className="text-lg font-semibold text-amber-100 mb-3">Date de contact:</h4>
              <ul className="space-y-2 text-amber-100/80">
                <li>• <strong>Asociația Culturală Dobrogenius</strong></li>
                <li>• Email: <a href="mailto:contact@dobrogenius.ro" className="text-amber-400 hover:text-amber-300 transition-colors">contact@dobrogenius.ro</a></li>
                <li>• Website: casa-cartarum.ro</li>
              </ul>
            </div>

            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">1.2 Acceptarea Termenilor</h3>
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Prin accesarea și utilizarea platformei Casa Cartarum, confirmați că ați citit, înțeles și acceptat prezentii Termeni și Condiții în totalitate.
            </p>

            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">1.3 Modificări ale Termenilor</h3>
            <p className="text-amber-100/80 leading-relaxed">
              Ne rezervăm dreptul de a modifica acești Termeni și Condiții în orice moment. Modificările vor fi publicate pe această pagină.
            </p>
          </section>

          {/* Section 2: Despre Platforma */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              2. DESPRE PLATFORMA CASA CARTARUM
            </h2>
            
            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">2.1 Scop și Misiune</h3>
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Casa Cartarum este o platformă culturală și educațională dedicată:
            </p>
            <ul className="list-disc list-inside space-y-2 text-amber-100/80 mb-6 ml-4">
              <li>Prezervării și promovării patrimoniului cartografic istoric românesc</li>
              <li>Punerii la dispoziție a publicului a hărților istorice digitalizate</li>
              <li>Oferirea de resurse educaționale (cronici istorice, audio-ghiduri, explorare 3D)</li>
              <li>Educației culturale și istorice a publicului</li>
            </ul>

            <h3 className="text-2xl font-semibold text-amber-100/90 mb-4">2.2 Conținut Disponibil</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="bg-amber-900/20 p-5 rounded-lg border border-amber-800/30">
                <h4 className="font-semibold text-amber-100 mb-2">Hărți istorice 3D</h4>
                <p className="text-amber-100/70 text-sm">Reprezentări digitale ale hărților istorice</p>
              </div>
              <div className="bg-amber-900/20 p-5 rounded-lg border border-amber-800/30">
                <h4 className="font-semibold text-amber-100 mb-2">Texte antice</h4>
                <p className="text-amber-100/70 text-sm">Documente istorice digitalizate</p>
              </div>
              <div className="bg-amber-900/20 p-5 rounded-lg border border-amber-800/30">
                <h4 className="font-semibold text-amber-100 mb-2">Audio-ghiduri</h4>
                <p className="text-amber-100/70 text-sm">Rezumate audio despre hărți</p>
              </div>
              <div className="bg-amber-900/20 p-5 rounded-lg border border-amber-800/30">
                <h4 className="font-semibold text-amber-100 mb-2">GIS Explorer 3D</h4>
                <p className="text-amber-100/70 text-sm">Explorare geografică interactivă</p>
              </div>
            </div>
          </section>

          {/* Section 3: Utilizarea Platformei */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              3. UTILIZAREA PLATFORMEI
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-green-900/10 border border-green-700/30 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-green-400 mb-4">Utilizare Permisă</h3>
                <ul className="space-y-2 text-amber-100/80 text-sm">
                  <li>• Vizualizare pentru uz personal și educațional</li>
                  <li>• Partajare link-uri către platformă</li>
                  <li>• Utilizare în context educațional (cu citare)</li>
                </ul>
              </div>

              <div className="bg-red-900/10 border border-red-700/30 rounded-lg p-6">
                <h3 className="text-xl font-semibold text-red-400 mb-4">Utilizare Interzisă</h3>
                <ul className="space-y-2 text-amber-100/80 text-sm">
                  <li>• Reproducere comercială fără permisiune</li>
                  <li>• Modificare conținut fără autorizare</li>
                  <li>• Scraping automat</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4: Proprietate Intelectuală */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              4. DREPTURI DE PROPRIETATE INTELECTUALĂ
            </h2>
            
            <div className="bg-amber-900/20 p-6 rounded-lg border border-amber-800/30 mb-6">
              <p className="text-amber-100/80 leading-relaxed">
                Toate drepturile aparțin <strong>Asociației Culturale Dobrogenius</strong>.
              </p>
            </div>
          </section>

          {/* Section 5: Protecția Datelor */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              5. PROTECȚIA DATELOR PERSONALE
            </h2>
            
            <p className="text-amber-100/80 leading-relaxed mb-4">
              Prelucrăm datele în conformitate cu <strong>GDPR (UE 2016/679)</strong> și legislația română.
            </p>

            <Link 
              to="/politica-confidentialitate" 
              className="inline-block bg-amber-700 hover:bg-amber-600 text-white px-6 py-3 rounded-lg transition-colors font-semibold"
              data-testid="link-to-privacy"
            >
              Vezi Politica de Confidențialitate →
            </Link>
          </section>

          {/* Section 6: Contact */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-amber-100 mb-6 border-b border-amber-800/30 pb-3">
              6. CONTACT
            </h2>
            
            <div className="bg-gradient-to-br from-amber-900/20 to-amber-800/10 p-8 rounded-lg border border-amber-700/30">
              <h3 className="text-2xl font-semibold text-amber-100 mb-4">Asociația Culturală Dobrogenius</h3>
              <div className="space-y-3 text-amber-100/80">
                <p className="flex items-center gap-3">
                  <span className="text-amber-400">📧</span>
                  <a href="mailto:contact@dobrogenius.ro" className="text-amber-300 hover:text-amber-200">
                    contact@dobrogenius.ro
                  </a>
                </p>
                <p className="flex items-center gap-3">
                  <span className="text-amber-400">🌐</span>
                  <span>casa-cartarum.ro</span>
                </p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="mt-16 pt-8 border-t border-amber-800/30 text-center">
            <p className="text-amber-100/60 text-sm mb-4">
              Ultima actualizare: 15 februarie 2026
            </p>
            <p className="text-amber-100/40 text-xs">
              © 2026 Asociația Culturală Dobrogenius
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default TermeniConditii;
